import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import ProgressBar from "./ProgressBar";
import { useSnackbar } from "./SnackbarHandler";
import HelpDialog from "./HelpDialog";

/**
 * A reusable loading dialog with progress bar
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls if the dialog is shown
 * @param {function} props.onClose - Function called when dialog is closed
 * @param {number} props.progress - Progress value (0-100)
 * @param {string} props.progressLabel - Label text for the progress bar
 * @param {string} props.title - Title text displayed above the progress bar
 * @param {boolean} props.disableBackdropClick - If true, clicking outside won't trigger onClose
 * @param {string} props.snackbarMessage - Message to show in snackbar when backdrop is clicked
 * @param {string} props.snackbarSeverity - Severity of the snackbar message ('success', 'error', 'warning', 'info')
 * @param {string} props.progressBarHeight - Height of the progress bar
 * @param {string} props.progressBarFontSize - Font size for the progress bar label
 * @returns {JSX.Element}
 */
const LoadingDialog = ({
  open,
  onClose,
  progress = 0,
  progressLabel = "Loading...",
  title = "Processing...",
  disableBackdropClick = false,
  snackbarMessage = "Processing will be completed soon. Please wait.",
  snackbarSeverity = "info",
  progressBarHeight = "15px",
  progressBarWidth = "100%",
  progressBarFontSize = "0.9rem",
  hideProgressBar = false,
  user,
  coffee = false,
}) => {
  const { showSnackbar } = useSnackbar();
  const [openOverviewDialog, setOpenOverviewDialog] = useState(false);
  const handleBackdropClick = (event) => {
    event.stopPropagation();
    if (!disableBackdropClick && onClose) {
      showSnackbar(snackbarMessage, snackbarSeverity);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClick={handleBackdropClick}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: { xs: "100%", md: "600px" },
            minHeight: { xs: "30%", md: "350px" },
            backgroundColor: "background.paper",
            boxShadow: 24,
            borderRadius: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          onClick: (e) => e.stopPropagation(), // Prevent click from bubbling to backdrop
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <svg width={0} height={0}>
              <defs>
                <linearGradient
                  id="my_gradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#e01cd5" />
                  <stop offset="100%" stopColor="#1CB5E0" />
                </linearGradient>
              </defs>
            </svg>

            {/* CircularProgress with gradient */}
            <CircularProgress
              size={58}
              sx={{
                "svg circle": { stroke: "url(#my_gradient)" },
              }}
            />

            <Typography
              variant="h5"
              sx={{
                textAlign: "center",
                fontWeight: "600",
                fontSize: "1.3rem",
                mt: 3,
                mb: 5,
              }}
            >
              {title}
            </Typography>
            {!hideProgressBar && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  //display: "none",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <ProgressBar
                  progress={progress}
                  label={progressLabel}
                  height={progressBarHeight}
                  fontSize={progressBarFontSize}
                  progressBarWidth={progressBarWidth}
                />
              </Box>
            )}
            {coffee && (
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: "center",
                  fontWeight: "500",
                  fontSize: "1rem",
                  mt: 1,
                  color: "text.secondary",
                }}
              >
                Hämta en god kopp{" "}
                <Box component="span" sx={{ fontSize: "1.3rem" }}>
                  ☕
                </Box>{" "}
                eller{" "}
                <Box
                  component="span"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    textDecoration: "none",
                    display: "inline",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => setOpenOverviewDialog(true)}
                >
                  utforska våra andra funktioner
                </Box>{" "}
                medans du väntar.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <HelpDialog
        open={openOverviewDialog}
        onClose={() => setOpenOverviewDialog(false)}
        overview={true}
        user={user}
      />
    </>
  );
};

export default LoadingDialog;
